import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Plus, X, Download } from 'lucide-react';
import { downloadPdf } from '../../utils/downloadPdf';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminInvoices() {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ user_id: '', items: '', total: '', currency: 'AOA', due_date: '', status: 'pending' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/invoices`).then(r => setInvoices(r.data)).catch(() => {});
    axios.get(`${API}/api/admin/users`).then(r => setUsers(r.data.filter((u: any) => u.role === 'client'))).catch(() => {});
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const items = form.items.split('\n').filter(Boolean).map(line => ({ description: line.trim(), amount: 0 }));
      const { data } = await axios.post(`${API}/api/admin/invoices`, {
        user_id: form.user_id, items, total: parseFloat(form.total) || 0,
        currency: form.currency, due_date: form.due_date, status: form.status,
      });
      setInvoices([data, ...invoices]);
      setShowCreate(false);
      setForm({ user_id: '', items: '', total: '', currency: 'AOA', due_date: '', status: 'pending' });
    } catch {} finally { setCreating(false); }
  };

  const updateStatus = async (id: string, status: string) => {
    await axios.put(`${API}/api/admin/invoices/${id}`, { status });
    setInvoices(invoices.map(i => i._id === id ? { ...i, status } : i));
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="admin" active="invoices" />
      <main data-testid="admin-invoices-page" style={{ flex: 1, background: 'var(--bg-portal)', padding: 32 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700 }}>{t('admin.invoices')}</h1>
            <button data-testid="create-invoice-btn" onClick={() => setShowCreate(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', textTransform: 'uppercase' }}>
              <Plus size={16} /> Create Invoice
            </button>
          </div>

          {/* Create modal */}
          {showCreate && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} onClick={() => setShowCreate(false)} />
              <div style={{ position: 'relative', background: 'white', border: '1px solid var(--border)', padding: 32, width: '100%', maxWidth: 480, zIndex: 51 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700 }}>Create Invoice</h2>
                  <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
                </div>
                <form onSubmit={handleCreate} data-testid="create-invoice-form">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Client *</label>
                      <select data-testid="invoice-user-select" value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})} required style={{...inputStyle, cursor: 'pointer'}}>
                        <option value="">Select client...</option>
                        {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Items (one per line) *</label>
                      <textarea data-testid="invoice-items" value={form.items} onChange={e => setForm({...form, items: e.target.value})} required rows={3} placeholder="Microsoft 365 E3&#10;Adobe Creative Cloud" style={{...inputStyle, resize: 'vertical' as const}} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={labelStyle}>Total *</label>
                        <input data-testid="invoice-total" type="number" step="0.01" value={form.total} onChange={e => setForm({...form, total: e.target.value})} required style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Currency</label>
                        <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                          {['AOA', 'USD'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={labelStyle}>Due Date</label>
                        <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Status</label>
                        <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                          {['pending', 'paid', 'unpaid'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <button type="submit" data-testid="invoice-submit-btn" disabled={creating}
                      style={{ padding: '12px', background: 'var(--primary)', color: 'white', fontWeight: 600, border: 'none', textTransform: 'uppercase', cursor: 'pointer', opacity: creating ? 0.6 : 1 }}>
                      {creating ? '...' : 'Create Invoice'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Table */}
          <div style={{ background: 'white', border: '1px solid var(--border)' }}>
            {invoices.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>{t('portal.no_invoices')}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['#', 'User', t('portal.amount'), t('portal.status'), t('portal.date'), 'PDF', t('portal.actions')].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv, i) => (
                      <tr key={inv._id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>INV-{String(i+1).padStart(4,'0')}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{inv.user_id}</td>
                        <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>{inv.currency} {inv.total?.toFixed(2)}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600, background: inv.status === 'paid' ? 'rgba(34,197,94,0.12)' : 'rgba(251,191,36,0.12)', color: inv.status === 'paid' ? '#16A34A' : '#D97706', textTransform: 'uppercase' }}>
                            {t(`status.${inv.status}`)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString() : '-'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => downloadPdf(`/api/admin/invoices/${inv._id}/pdf`, `invoice-${inv._id.slice(-8)}.pdf`)} data-testid={`admin-invoice-pdf-${inv._id}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 10, fontWeight: 600, background: 'rgba(13,71,161,0.08)', color: 'var(--primary)', border: '1px solid rgba(13,71,161,0.2)', cursor: 'pointer' }}>
                            <Download size={10} /> PDF
                          </button>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {inv.status !== 'paid' && <button onClick={() => updateStatus(inv._id, 'paid')} style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, background: 'rgba(34,197,94,0.12)', color: '#16A34A', border: '1px solid rgba(34,197,94,0.3)', cursor: 'pointer' }}>Mark Paid</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
